import * as React from 'react';
import {Link} from 'react-router-dom';
import {Table} from 'antd';
import {ColumnProps} from 'antd/lib/table';
import {
  ApplicationContextConsumer,
  bindLoadingHandler,
  LoadingOverlayHandler,
  withLoadingOverlay
} from '../app/ApplicationContext';
import {ErrorAlert} from '../common/ErrorAlert';
import SectionHeaderFormItem from '../common/form-items/SectionHeaderFormItem';
import {ErrorResponse, InternallyUsedInventoryStock, Item} from '../api/dtos/dtos';
import furoApi from '../api/FuroApi';
import locales from '../locales';
import * as paths from '../paths';
import {formatDateTimeOrNull} from '../utils/date-utils';
import * as tableUtils from '../utils/table-utils';
import {GridState, searchableSortableColumn, simpleColumn} from '../utils/table-utils';
import {renderFixedPriceColumn, renderPriceColumn, renderUnitColumn} from "../utils/number-utils";

interface Props {
  item: Item;
}

interface State extends GridState<InternallyUsedInventoryStock> {
  itemCount: number;
  totalCost: number;
  error?: ErrorResponse;
}

export default class InternallyUsedInventoryItemsTable extends React.Component<Props, State> {
  public readonly loadingHandler?: LoadingOverlayHandler;

  public state: State = {
    ...tableUtils.initialGridState(10),
    sortColumnKey: 'createdAt',
    sortOrder: 'descend',
    itemCount: 0,
    totalCost: 0,
  };

  public componentDidMount() {
    this.reload();
  }

  public render(): React.ReactNode {
    const {data, error} = this.state;

    const columns: Array<ColumnProps<InternallyUsedInventoryStock>> = [
      searchableSortableColumn(locales.inventoryNumber, 'inventoryNumber', this.state, this.renderInventoryNumber),
      searchableSortableColumn(locales.appellation, 'name', this.state, r => r.inventoryStock.name),
      searchableSortableColumn(locales.brand, 'brand', this.state, r => r.inventoryStock.brand),
      searchableSortableColumn(locales.type, 'type', this.state, r => r.inventoryStock.type),
      searchableSortableColumn(locales.created_at, 'createdAt', this.state, r => formatDateTimeOrNull(r.inventoryStock.createdAt) || '-'),
      simpleColumn(locales.entries, r => renderUnitColumn(r.count, locales.pcs)),
      simpleColumn(locales.total_cost, r => renderPriceColumn(r.total)),

    ];

    return (
      <>
        <ApplicationContextConsumer>
          {({loadingHandler}) => bindLoadingHandler(loadingHandler, this)}
        </ApplicationContextConsumer>

        <SectionHeaderFormItem title={locales.internally_used_inventory_items} style={{marginTop: 24}}/>

        <Table
          dataSource={data}
          columns={columns}
          rowKey={this.rowKey}
          bordered={true}
          size="small"
          pagination={tableUtils.defaultPaginationConfig(this.state)}
          onChange={tableUtils.handleOnGridChange(this.state, this.onGridChange)}
          scroll={{x: 768}}
          footer={this.renderTotalsFooter}
        />

        {error && <ErrorAlert style={{marginTop: 16}} error={error}/>}
      </>
    );
  }

  private rowKey = (record: InternallyUsedInventoryStock, index?: number): string => {
    if (record.inventoryStock.id != null) {
      return String(record.inventoryStock.id);
    }
    return `${record.inventoryStock.inventoryNumber}-${index || 0}`;
  };

  private renderInventoryNumber = (record: InternallyUsedInventoryStock): React.ReactNode => {
    const stock = record.inventoryStock;
    if (stock.id != null) {
      return <Link to={{
        pathname: paths.viewInventoryStockPath(stock.id), state: {
          stateFilter: {
            targetType: 'internallyUsed',
            item: this.props.item,
          },
        },
      }}>{stock.inventoryNumber}</Link>;
    }
    return stock.inventoryNumber;
  };

  private renderTotalsFooter = (): React.ReactNode => {
    const {itemCount, totalCost} = this.state;
    const width = 134;
    return (
      <div style={{display: 'flex', justifyContent: 'flex-end'}}>
        <div style={{textAlign: 'right', marginRight: width}}>
          {locales.total} <b>{itemCount}</b> {locales.entry}<br/>
          <b>{renderFixedPriceColumn(totalCost)}</b>
        </div>
      </div>
    );
  };


  private onGridChange = (newState: GridState<InternallyUsedInventoryStock>) => {
    this.setState({...newState}, this.reload);
  };

  private reload = () => {
    const request = tableUtils.toGridRequest(this.state);
    const promise = furoApi.items.getInternallyUsedInventoryStocks(this.props.item.id!, request)
      .then(response => this.setState({
        ...tableUtils.toGridState(response.pagedResponse),
        itemCount: response.stats.count,
        totalCost: response.stats.total,
        error: undefined
      }))
      .catch(this.onError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private onError = (error: ErrorResponse) => this.setState({error});
}




// WEBPACK FOOTER //
// ./src/item-details/InternallyUsedInventoryItemsTable.tsx